import { HttpClient } from "@angular/common/http";
import { Inject, Injectable, inject } from "@angular/core";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { getCurrentSiteId } from "@partikelart/identity/data-access-user";
import {
	BackendReport,
	BauteilWithReports,
	FrontendReport,
	FrontendReportForm,
	reportDictDE,
	reportDictEN,
} from "@partikelart/portal/shared-typing";
import {
	BehaviorSubject,
	combineLatest,
	concatMap,
	forkJoin,
	from,
	map,
	mergeAll,
	mergeMap,
	Observable,
	switchMap,
	toArray,
} from "rxjs";
import { read, utils, WorkBook, WorkSheet, writeFile } from "xlsx";
import { EXPORT_CONFIG } from "../export-config";

@Injectable({
	providedIn: "root",
})
export class DataExportService {
	private readonly config = inject(EXPORT_CONFIG);
	private bauteileForUsers: BehaviorSubject<BauteilWithReports[]> = new BehaviorSubject<BauteilWithReports[]>([]);
	private selectedBauteile: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
	private currentStep: BehaviorSubject<number> = new BehaviorSubject<number>(0);
	currentStep$ = this.currentStep.asObservable();

	private get backendBase(): string {
		return this.config.portalBackendUrl || this.config.url;
	}

	bauteileForUsers$ = this.bauteileForUsers.asObservable();
	finalSelection: FrontendReportForm[] = [];

	currentSelectedBauteile$ = this.selectedBauteile.asObservable();

	selectedReportsForExport$ = this.currentSelectedBauteile$.pipe(
		concatMap((number) => {
			const observables = number.map((id) =>
				this.http
					.get<{ data: BackendReport[] }>(`${this.backendBase}/api/reports/by-component/${id}`)
					.pipe(map((res) => res.data)),
			);
			return forkJoin(...observables);
		}),
		map((values) => values.flat()),
		map((reports) =>
			reports.map((report) => {
				return new FrontendReport(report);
			}),
		),
	);
	// Update State for Selected CustomerNOs here
	// ToDo Translate Service

	constructor(
		private http: HttpClient,
		private translate: TranslateService,
		private store: Store,
	) {}

	writeReportsToWorkbook(reports: FrontendReport[]): WorkBook {
		let currentTranslationDict = reportDictEN;
		if (this.translate.currentLang() === "de") {
			currentTranslationDict = reportDictDE;
		}

		const headerRow = Object.values(currentTranslationDict);
		let data = [headerRow];
		reports.forEach((report) => {
			const dataRow = Object.keys(currentTranslationDict).map((key) => {
				return report[key];
			});
			data = [...data, dataRow];
		});
		const worksheet = utils.aoa_to_sheet(data);
		const workbook = utils.book_new();
		utils.book_append_sheet(workbook, worksheet, "Daten");
		return workbook;
	}

	reportDataToFile(
		reports: FrontendReport[] = [],
		option = "xlsx",
		outputFileNameIdentifierType: OutputFilenameIdentifier = 0,
	): void {
		// This Function gets a report, extracts the correctly translated Report Attributes and return a SheetJS Worksheet
		let reportDataForExport: FrontendReport[] = [...reports];
		reportDataForExport = reportDataForExport.map((report) => {
			report.createExportSizeClasses();
			return report;
		});
		const workbook = this.writeReportsToWorkbook(reportDataForExport);
		const currentDate = new Date();
		let outputIdentifier = "";
		if (outputFileNameIdentifierType === OutputFilenameIdentifier.ReportNumber) {
			outputIdentifier = reports[0].analysisNo.toString();
		}
		if (outputFileNameIdentifierType === OutputFilenameIdentifier.SpecNumber) {
			outputIdentifier = reports[0].specNumber?.toString() ?? "";
		}
		writeFile(workbook, `${currentDate.toISOString().slice(0, 10)}_Export_${outputIdentifier}.${option}`);
	}
	getPossiblePartsForSite(siteId: string): void {
		this.http.get<BauteilWithReports[]>(`${this.backendBase}/api/sites/${siteId}/parts`).subscribe((result) => {
			this.bauteileForUsers.next(result);
		});
	}
	fetchPossiblePartsForSite(siteId: string): Observable<BauteilWithReports[]> {
		return this.http.get<BauteilWithReports[]>(`${this.backendBase}/api/sites/${siteId}/parts`);
	}
	selectOrDeselect(parts: BauteilWithReports[]): void {
		parts.forEach((part) => {
			const data = this.bauteileForUsers.value;
			const index = data.findIndex((value) => value.artnr == part.artnr);
			data[index].selected = !data[index].selected;
			this.bauteileForUsers.next(data);

			const artnr = part.artnr;
			const selectedPartArtnrs = this.selectedBauteile.getValue();
			if (selectedPartArtnrs.includes(artnr)) {
				this.selectedBauteile.next(selectedPartArtnrs.filter((selectedArtnr) => selectedArtnr !== artnr));
			} else {
				this.selectedBauteile.next(selectedPartArtnrs.concat(artnr));
			}
		});
	}
	selectAll(parts: BauteilWithReports[]) {
		this.selectedBauteile.next(this.bauteileForUsers.value.map((part) => part.artnr));
	}
	deselectAll() {
		this.selectedBauteile.next([]);
	}
	setCurrentStep(index: number) {
		this.currentStep.next(index);
	}
	setFinalReportsSelection(reports: FrontendReportForm[]) {
		this.finalSelection = reports;
	}
	getLimitData() {
		return this.finalSelection[0];
	}
	exportCurrent() {
		this.selectedReportsForExport$.subscribe((data) => this.reportDataToFile(data));
	}
	removeAll(): void {
		this.selectedBauteile.next([]);
	}
	getReportsForComponent(artnr: string): Observable<FrontendReport[]> {
		return this.store.select(getCurrentSiteId).pipe(
			switchMap((siteId) => {
				return this.http
					.get<{ data: BackendReport[] }>(`${this.backendBase}/api/reports/by-component/${artnr}`, {
						params: siteId ? { siteId } : {},
					})
					.pipe(map((res) => res.data));
			}),
			map((reports) =>
				reports.map((report) => {
					return new FrontendReport(report);
				}),
			),
		);
	}
}

export enum OutputFilenameIdentifier {
	SpecNumber,
	ReportNumber,
}
